export default class BrandPage {
  constructor() {
    // GSAP와 ScrollTrigger가 전역으로 로드되어 있는지 확인
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      console.warn("BrandPage: GSAP or ScrollTrigger not found");
      return;
    }
    
    gsap.registerPlugin(ScrollTrigger);
    
    this.root = document.querySelector(".no-brand-hero");
    if (!this.root) {
      this.root = document.querySelector("section[class*='brand']");
      if (!this.root) return;
    }

    this.hero = document.querySelector(".no-brand-hero");
    this.heroImage = document.querySelector("[data-hero-image]");
    this.heroTitle = document.querySelector("[data-hero-title]");
    this.heroTitleLines = document.querySelectorAll(".no-brand-hero__title-line");
    this.heroTitleTexts = document.querySelectorAll(".no-brand-hero__title-text");
    this.heroScroll = document.querySelector("[data-hero-scroll]");
    
    this.intro = document.querySelector(".no-brand-intro");
    this.introEyebrow = document.querySelector("[data-intro-eyebrow]");
    this.introEyebrowText = document.querySelector(".no-brand-intro__eyebrow-text");
    this.introTitle = document.querySelector("[data-intro-title]");
    this.introTitleText = document.querySelector(".no-brand-intro__title-text");
    this.introLead = document.querySelector("[data-intro-lead]");
    this.introLeadText = document.querySelector(".no-brand-intro__lead-text");
    this.introImage = document.querySelector("[data-intro-image]");
    
    this.philosophy = document.querySelector(".no-brand-philosophy");
    this.philosophyTitle = document.querySelector("[data-philosophy-title]");
    this.philosophyTitleText = document.querySelector(".no-brand-philosophy__title-text");
    this.philosophyItems = gsap.utils.toArray("[data-philosophy-item]");
    
    this.values = document.querySelector(".no-brand-values");
    this.valuesTitle = document.querySelector("[data-values-title]");
    this.valuesTitleText = document.querySelector(".no-brand-values__title-text");
    this.valuesHint = document.querySelector("[data-values-hint]");
    this.valuesHintText = document.querySelector(".no-brand-values__hint-text");
    this.valueItems = gsap.utils.toArray("[data-value-item]");

    this.init();
  }

  init() {
    this.setInitial();
    this.setHeroAnimation();
    this.setIntroAnimation();
    this.setPhilosophyAnimation();
    this.setValuesAnimation();
    this.setParallaxEffects();
    this.setHoverEffects();
  }

  setInitial() {
    // Hero 초기 상태
    if (this.heroTitleTexts) {
      gsap.set(this.heroTitleTexts, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.heroScroll) {
      gsap.set(this.heroScroll, { opacity: 0 });
    }
    
    // Hero 이미지 초기 클립패스 설정
    if (this.heroImage) {
      const heroImg = this.heroImage.querySelector("img");
      if (heroImg) {
        gsap.set(heroImg, {
          clipPath: "polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)"
        });
      }
    }

    // Intro 초기 상태
    if (this.introEyebrowText) {
      gsap.set(this.introEyebrowText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.introTitleText) {
      gsap.set(this.introTitleText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.introLeadText) {
      gsap.set(this.introLeadText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    // Intro 이미지 초기 clip-path 설정
    if (this.introImage) {
      const introImg = this.introImage.querySelector("img");
      if (introImg) {
        gsap.set(introImg, {
          clipPath: "polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)"
        });
      }
    }

    // Philosophy 초기 상태
    if (this.philosophyTitleText) {
      gsap.set(this.philosophyTitleText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.philosophyItems) {
      gsap.set(this.philosophyItems, { 
        opacity: 0, 
        y: 40
      });
    }

    // Values 초기 상태
    if (this.valuesTitleText) {
      gsap.set(this.valuesTitleText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.valuesHintText) {
      gsap.set(this.valuesHintText, { 
        opacity: 0, 
        y: "100%"
      });
    }
    if (this.valueItems) {
      gsap.set(this.valueItems, { 
        opacity: 0, 
        y: 30
      });
    }
  }

  setHeroAnimation() {
    if (!this.hero) return;

    // 타이틀 라인 나타내기 (절제된 애니메이션)
    if (this.heroTitleTexts && this.heroTitleTexts.length > 0) {
      const tl = gsap.timeline({
        delay: 0.3,
      });

      this.heroTitleTexts.forEach((text, index) => {
        tl.to(text, {
          opacity: 1,
          y: "0%",
          duration: 1,
          ease: "power2.out",
        }, index === 0 ? 0 : "-=0.6");
      });
    }

    // 스크롤 인디케이터 애니메이션
    if (this.heroScroll) {
      gsap.to(this.heroScroll, {
        opacity: 1,
        delay: 1.5,
        duration: 0.6,
        ease: "power2.out",
      });
    }
  }

  setIntroAnimation() {
    if (!this.intro) return;

    const masterTl = gsap.timeline({
      scrollTrigger: {
        trigger: this.intro,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // Eyebrow 마스크 애니메이션
    if (this.introEyebrowText) {
      masterTl.to(this.introEyebrowText, {
        opacity: 1,
        y: "0%",
        duration: 0.6,
        ease: "power2.out",
      });
    }

    // Title 마스크 애니메이션
    if (this.introTitleText) {
      masterTl.to(this.introTitleText, {
        opacity: 1,
        y: "0%",
        duration: 0.8,
        ease: "power2.out",
      }, "-=0.4");
    }

    // Lead 마스크 애니메이션
    if (this.introLeadText) {
      masterTl.to(this.introLeadText, {
        opacity: 1,
        y: "0%",
        duration: 0.8,
        ease: "power2.out",
      }, "-=0.6");
    }

    // 이미지 clip-path 애니메이션 (메인 히어로와 동일한 스타일)
    if (this.introImage) {
      const introImg = this.introImage.querySelector("img");
      if (introImg) {
        masterTl.to(introImg, {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          duration: 1.5,
          ease: "power4.out",
        }, "-=0.4");
      }
    }
  }

  setPhilosophyAnimation() {
    if (!this.philosophy) return;

    // 타이틀 마스크 애니메이션
    if (this.philosophyTitleText) {
      gsap.to(this.philosophyTitleText, {
        opacity: 1,
        y: "0%",
        scrollTrigger: {
          trigger: this.philosophyTitle,
          start: "top 80%",
          toggleActions: "play none none none",
        },
        duration: 0.8,
        ease: "power2.out",
      });
    }

    // 아이템 애니메이션 (타임라인으로 정교하게)
    if (this.philosophyItems && this.philosophyItems.length > 0) {
      const itemTl = gsap.timeline({
        scrollTrigger: {
          trigger: this.philosophy,
          start: "top 70%",
          toggleActions: "play none none none",
        },
      });

      this.philosophyItems.forEach((item, index) => {
        itemTl.to(item, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
        }, index * 0.15);
      });
    }
  }

  setValuesAnimation() {
    if (!this.values) return;

    const headerTl = gsap.timeline({
      scrollTrigger: {
        trigger: this.values,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // 타이틀 마스크 애니메이션
    if (this.valuesTitleText) {
      headerTl.to(this.valuesTitleText, {
        opacity: 1,
        y: "0%",
        duration: 0.8,
        ease: "power2.out",
      });
    }

    // 힌트 마스크 애니메이션
    if (this.valuesHintText) {
      headerTl.to(this.valuesHintText, {
        opacity: 1,
        y: "0%",
        duration: 0.6,
        ease: "power2.out",
      }, "-=0.6");
    }

    // 아이템 애니메이션 (타임라인으로 정교하게)
    if (this.valueItems && this.valueItems.length > 0) {
      const itemTl = gsap.timeline({
        scrollTrigger: {
          trigger: this.values,
          start: "top 70%",
          toggleActions: "play none none none",
        },
      });

      this.valueItems.forEach((item, index) => {
        itemTl.to(item, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        }, index * 0.08);
      });
    }
  }

  setParallaxEffects() {
    if (!this.hero || !this.heroImage) return;

    // 히어로 이미지 클립패스 스크롤 애니메이션
    const heroImg = this.heroImage.querySelector("img");
    if (heroImg) {
      gsap.to(heroImg, {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        scrollTrigger: {
          trigger: this.hero,
          start: "center bottom",
          end: "center top",
          scrub: 0.8,
          // markers: true,
        },
        ease: "power4.out",
      });
    }

    // 인트로 이미지 패럴랙스 (절제된 효과)
    if (this.introImage) {
      const introImg = this.introImage.querySelector("img");
      if (introImg) {
        gsap.to(introImg, {
          scale: 1.05,
          scrollTrigger: {
            trigger: this.introImage,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        });
      }
    }

    // 히어로 타이틀 페이드아웃 (절제된 효과)
    if (this.heroTitle) {
      gsap.to(this.heroTitle, {
        y: -40,
        opacity: 0,
        scrollTrigger: {
          trigger: this.hero,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }

    // 스크롤 인디케이터 페이드아웃
    if (this.heroScroll) {
      gsap.to(this.heroScroll, {
        opacity: 0,
        y: 10,
        scrollTrigger: {
          trigger: this.hero,
          start: "top top",
          end: "center top",
          scrub: 1,
        },
      });
    }
  }

  setHoverEffects() {
    // Philosophy 아이템 호버 효과 제거 (CSS로 처리)
  }
}
